import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import cx from 'classnames';
import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EngagementType } from '@/__generated__/graphql';
import { usePendingProjectInvite } from '@/auth/components/SelfServiceSMBSignupModal/hooks/usePendingProjectInvite';
import type { PendingProjectInvite } from '@/auth/components/SelfServiceSMBSignupModal/types';
import {
  clearSubscriptionIdempotencyKey,
  getOrCreateSubscriptionIdempotencyKey,
} from '@/auth/components/SelfServiceSMBSignupModal/utils/idempotency';
import { captureSelfServiceError } from '@/auth/components/SelfServiceSMBSignupModal/utils/sentryScrub';
import { usePendingInviteGuard } from '@/auth/hooks/usePendingInviteGuard';
import {
  ACTIVE_PROJECT_MEMBERSHIP_QUERY,
  COMPLETE_SMB_SIGNUP_MUTATION,
  PENDING_PROJECT_INVITE_QUERY,
  REQUEST_CONSULTATION_MUTATION,
} from '@/auth/store/queries';
import { authSession } from '@/auth/utils/authSession';
import {
  MFA_SETUP_AT_LOGIN_MESSAGE,
  MfaSetupAtLoginError,
  mfaSetupAtLogin,
} from '@/auth/utils/cognitoMfaSetup';
import {
  CognitoSessionEndedError,
  type SessionOrigin,
  captureSessionOrigin,
  sessionTokenOr,
} from '@/auth/utils/cognitoSession';
import Button from '@/components/Button';
import CircularProgress from '@/components/CircularProgress';
import FAIcon from '@/components/Icon/FAIcon';
import Link from '@/components/Link';
import StripePaymentMethodForm from '@/components/StripePaymentMethodForm';
import ConfirmDialog from '@/componentsv2/ConfirmDialog';
import config from '@/config';
import { ERROR_CODES, GraphQLClient, hasErrorCode } from '@/core/api';
import { getDefaultErrorMessage } from '@/core/apiErrorMessages';
import { useRateDisplay } from '@/hooks/useRateDisplay';
import { RATE_UNAVAILABLE_LABEL, RateDisplayType, toCreditsPerHour } from '@/utils/rateDisplay';

import { RATE_UNAVAILABLE_SUBMIT_MESSAGE, SMB_SIGNUP_CONFIG } from '../constants';
import { useStyles } from '../styles';
import type {
  BillingAccountInfo,
  CompleteSMBSignupResult,
  Expert,
  PaymentProgress,
  SchedulingData,
} from '../types';
import { getErrorCode, getErrorMessage } from '../utils/errorHandling';
import { hasCardOnFile } from '../utils/paymentProgress';
import {
  trackSMBError,
  trackSMBPaymentMethodAdded,
  trackSMBPaymentStarted,
  trackSMBSignupComplete,
} from '../utils/smbAnalytics';

interface PaymentStepProps {
  clientSecret: string | null;
  stripeCustomerId: string | null;
  isProcessing: boolean;
  setIsProcessing: (v: boolean) => void;
  // ENG-5549: progress past Stripe's confirmSetup, held by the modal (see PaymentProgress).
  paymentProgress: PaymentProgress;
  onPaymentProgressChange: (update: Partial<PaymentProgress>) => void;
  paymentError: string | null;
  onSuccess: () => void;
  onError: (message: string) => void;
  onBack: () => void;
  // V2: Consultation details for order summary
  expert: Expert;
  duration: number; // in minutes
  schedulingData: SchedulingData | null;
  landingUrl?: string;
  // OTP Flow: Returning user props
  isReturningUser?: boolean;
  userName?: string;
  userCompany?: string;
  billingAccount?: BillingAccountInfo;
  groupId?: string;
  // Existing user without billing - needs Stripe form but skip user creation
  isExistingUserWithoutBilling?: boolean;
  // OTP token for completeSMBSignup (optional - if absent, uses Bearer auth)
  otpVerificationToken?: string | null;
  // Analytics: user ID and modal open time for tracking
  userId?: string;
  modalOpenTime?: Date;
  // ENG-5334 follow-up: navigation destination for the
  // active_membership_exists guard branch below, mirroring
  // SubscriptionStep.tsx's onGoToProjects prop. No project id/slug is
  // available for that error code, so this always lands the user on their
  // expert requests rather than a specific project. Optional — falls back
  // to a direct /expert_requests navigation when the host doesn't supply
  // one (no call site currently wires this prop in).
  onGoToProjects?: () => void;
}

// Helper function to parse expertRequestId from landing URL
function parseExpertRequestId(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('expertRequestId') || undefined;
  } catch {
    return undefined;
  }
}

// Shared copy for both the early (pre-card-entry) and late (completeSMBSignup
// guard) active-membership dialogs below — kept in one place so the two stay
// in sync. Mirrors SubscriptionStep.tsx's copy of the same dialogs.
const ACTIVE_MEMBERSHIP_TITLE = "You're already a project member";
const ACTIVE_MEMBERSHIP_MESSAGE_LINE_1 =
  "You don't need to subscribe to view your current projects.";
const ACTIVE_MEMBERSHIP_MESSAGE_LINE_2 =
  'Want to start a new account and create your own projects? You can still set up a subscription.';
const GO_TO_PROJECTS_LABEL = 'Go to my projects';

// ENG-5549: outcomes after the card is confirmed that a retry cannot change. The api has
// already emailed an unregistered expert, so a retry emails them again (ENG-5322); an
// unpriceable expert needs support. billing.unauthorized is completeSMBSignup's partner-path
// admin gate: it refuses a Bearer user who is not a pending self-service signup (an existing
// account without billing) on the first attempt, so it does not mean the signup went
// through. The code lists are built per call so importing this module reads nothing from
// ERROR_CODES, as before.
const BILLING_SETUP_REFUSED_MESSAGE =
  "We couldn't set up billing for this account here. Please contact support to finish booking.";

// ENG-6106: the Cognito session ended after completeSMBSignup went through, so
// the account exists and only a fresh sign-in can finish the booking.
const SIGN_IN_AGAIN_MESSAGE =
  'Your account is set up, but your session ended. Please sign in again to finish booking.';

// ENG-6106: the session each completeSMBSignup result was minted under. A
// retry of the steps after it reuses the stored result, and must not sign in
// with its minted token once that Cognito session has ended.
const signupOrigins = new WeakMap<CompleteSMBSignupResult, SessionOrigin>();

// Blocked reasons that only a visit to the login page can clear.
const SIGN_IN_REASONS = [SIGN_IN_AGAIN_MESSAGE, MFA_SETUP_AT_LOGIN_MESSAGE];

function getRetryBlockedReason(err: unknown): string | null {
  if (err instanceof CognitoSessionEndedError) return SIGN_IN_AGAIN_MESSAGE;
  if (err instanceof MfaSetupAtLoginError) return err.message;
  const code = getErrorCode(err);
  if (!code) return null;
  if (code === ERROR_CODES.BILLING_UNAUTHORIZED) return BILLING_SETUP_REFUSED_MESSAGE;
  const finalCodes: string[] = [
    ERROR_CODES.AUTH_ACCOUNT_EXISTS_LOGIN_REQUIRED,
    ERROR_CODES.CONSULTATION_UNREGISTERED_EXPERT_REQUEST,
    ERROR_CODES.BILLING_TAKE_RATE_UNAVAILABLE,
    ERROR_CODES.BILLING_SITE_TAKE_RATE_UNAVAILABLE,
    ERROR_CODES.BILLING_INVALID_CREDIT_PRICING,
    ERROR_CODES.BILLING_INVALID_SITE_CREDIT_PRICING,
  ];
  if (finalCodes.includes(code)) return getErrorMessage(err);
  return null;
}

export function PaymentStep({
  clientSecret,
  stripeCustomerId: _stripeCustomerId,
  isProcessing,
  setIsProcessing,
  paymentProgress,
  onPaymentProgressChange,
  paymentError,
  onSuccess,
  onError,
  onBack,
  expert,
  duration,
  schedulingData,
  landingUrl: _landingUrl,
  // OTP Flow props
  isReturningUser = false,
  userName,
  userCompany,
  billingAccount,
  groupId,
  isExistingUserWithoutBilling = false,
  otpVerificationToken,
  // Analytics props
  userId,
  modalOpenTime,
  onGoToProjects,
}: PaymentStepProps): JSX.Element {
  const s = useStyles();
  // No local readiness gate: StripePaymentMethodForm renders its own loading and
  // failure chrome. Gating this pane on PaymentElement's onReady meant a
  // Stripe.js failure left the whole step behind an opaque spinner with the
  // error message present but unpaintable (Sentry WEBAPP-CCB).
  const displayType = useRateDisplay();
  // Idempotency guard: prevent multiple concurrent calls to handleStripeSuccess
  const isCompletingSignup = useRef(false);
  // Track when payment screen is first shown for duration calculation
  const paymentStartTime = useRef<Date>(new Date());

  // ENG-5334 code-review follow-up: idempotency key for completeSMBSignup so
  // a double-fire (rapid double-click, or a client retry after a network
  // timeout that actually succeeded server-side) can't create two billing
  // accounts/Stripe subscriptions. Mirrors SubscriptionStep.tsx's approach
  // (mint once via getOrCreateSubscriptionIdempotencyKey, reuse the same
  // key on every retry, clear on success) but held in a ref instead of
  // flow state, matching this file's plain-refs pattern. Scope prefers
  // userId and falls back to the OTP token so a key can still be minted
  // before userId is set.
  const idempotencyScope = userId || otpVerificationToken || '';
  const idempotencyKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!idempotencyScope || idempotencyKeyRef.current) return;
    idempotencyKeyRef.current = getOrCreateSubscriptionIdempotencyKey(idempotencyScope);
  }, [idempotencyScope]);

  // Determine if displaying credits or dollars based on cookie/UTM
  const isCreditsPayment = displayType === RateDisplayType.CREDITS;

  // Calculate trial end date (14-day trial where today counts as day 1)
  const trialEndDate = moment().add(13, 'days').format('MMM D, YYYY');

  // Track payment started when component mounts
  useEffect(() => {
    const duration = modalOpenTime
      ? Math.round((new Date().getTime() - modalOpenTime.getTime()) / 1000)
      : 0;

    trackSMBPaymentStarted(duration, isReturningUser, userId || '', expert.id);
  }, [modalOpenTime, isReturningUser, userId, expert.id]);

  // Calculate consultation price based on expert client rate (with take rate applied) and
  // duration. Mirrors calculateCreditPrice below: null means the API could not price the
  // expert. billRate is deliberately not substituted — it is the rate BEFORE the platform
  // take rate, so quoting it understates the charge and discloses the expert's take-home
  // (ENG-5571).
  const calculatePrice = () => {
    const hourlyRateCents = expert.clientRate;
    if (hourlyRateCents === null || hourlyRateCents === undefined) return null;

    const durationHours = duration / 60;
    const totalCents = Math.round(hourlyRateCents * durationHours);
    const dollars = Math.round(totalCents / 100);
    return dollars.toString();
  };

  // Calculate credit price using expert.creditRate (stored in hundredths, e.g. 83 = 0.83
  // credits/hr). Null means the API could not price the expert — surface that instead of
  // "0.00 credits", which reads as a free call (ENG-5283).
  const calculateCreditPrice = () => {
    const creditRatePerHour = toCreditsPerHour(expert.creditRate);
    if (creditRatePerHour === null) return null;

    const durationHours = duration / 60;
    return (creditRatePerHour * durationHours).toFixed(2);
  };

  // A price we cannot compute must not be submittable. requestConsultation hard-fails on an
  // unpriceable expert (billing.take_rate_unavailable), and on the new-user path that failure
  // lands *after* completeSMBSignup has created the billing account, attached the card and
  // started the trial — so the user would finish signup and get no consultation, the one
  // thing they came for. Blocking with an explanation is strictly better (ENG-5571).
  //
  // Gate on clientRate regardless of the displayed currency: every path out of this step ends
  // in completeSMBSignup, which creates a USD/SMB account, so requestConsultation prices
  // against client_rate whatever the viewer is *shown*. Keying this on the credit rate instead
  // would both let a null client_rate through under CREDITS display and falsely block an
  // anonymous viewer, whose credit_rate is null purely for lack of `view_credit_rate`.
  const rateUnavailable = calculatePrice() === null;

  const renderRateUnavailableNotice = () => {
    if (!rateUnavailable) return null;

    return (
      <div className={s.paymentError}>
        <p className={s.paymentErrorText}>{RATE_UNAVAILABLE_SUBMIT_MESSAGE}</p>
      </div>
    );
  };

  // Calls completeSMBSignup and returns the raw result. Throws on failure —
  // callers (handleStripeSuccess and the pending-invite guard's retry,
  // below) handle errors themselves.
  const submitCompleteSMBSignup = useCallback(
    async (overridePendingInviteGuard: boolean) => {
      // Chokepoint for every account-creating path, including the pending-invite guard's
      // retry. `submitDisabled` only reaches the button, so a form-level submit can still
      // arrive here with an unpriceable expert (ENG-5571).
      if (rateUnavailable) {
        throw new Error(RATE_UNAVAILABLE_SUBMIT_MESSAGE);
      }

      const client = otpVerificationToken
        ? new GraphQLClient(config.apiUrl) // OTP flow: unauthenticated client
        : authSession.createClient(); // Bearer flow: authenticated client

      // Reuse the same key across retries (including the pending-invite
      // guard's retry below) rather than minting a fresh one per call.
      const idempotencyKey =
        idempotencyKeyRef.current ?? getOrCreateSubscriptionIdempotencyKey(idempotencyScope);
      idempotencyKeyRef.current = idempotencyKey;

      const { data: completeData } = await client.client.mutate({
        mutation: COMPLETE_SMB_SIGNUP_MUTATION,
        // ENG-6106: mfaSetupAtLogin answers the MFA gate, not mfaGateLink.
        context: { skipMfaGateRedirect: true },
        variables: {
          input: {
            otpVerificationToken: otpVerificationToken || undefined,
            seatCount: SMB_SIGNUP_CONFIG.SEAT_COUNT,
            billingInterval: SMB_SIGNUP_CONFIG.BILLING_INTERVAL,
            idempotencyKey,
            ...(overridePendingInviteGuard ? { overridePendingInviteGuard: true } : {}),
          },
        },
      });

      const result = completeData?.completeSMBSignup;
      if (!result?.token) {
        throw new Error('Failed to complete signup');
      }
      return result;
    },
    [otpVerificationToken, idempotencyScope, rateUnavailable]
  );

  // Analytics for a completeSMBSignup that has just returned. Kept out of finishSignup
  // so that retrying the steps after it doesn't count the signup twice (ENG-5549).
  const trackSignupCompleted = (
    result: CompleteSMBSignupResult,
    paymentMethodDetails?: { cardBrand?: string }
  ) => {
    // Track payment method added (after successful completeSMBSignup)
    const paymentDuration = Math.round(
      (new Date().getTime() - paymentStartTime.current.getTime()) / 1000
    );
    const cardBrand = paymentMethodDetails?.cardBrand || 'unknown';
    trackSMBPaymentMethodAdded(cardBrand, userId || '', expert.id, paymentDuration);

    // Track signup complete (billing account created)
    const totalDuration = modalOpenTime
      ? Math.round((new Date().getTime() - modalOpenTime.getTime()) / 1000)
      : 0;
    const resultUserId = result.user?.id || userId || '';
    const billingAccountId = result.billingAccount?.id || '';

    trackSMBSignupComplete(
      totalDuration,
      isReturningUser,
      expert.id,
      resultUserId,
      billingAccountId
    );
  };

  // Post-completeSMBSignup bookkeeping: authenticate(), the consultation request,
  // and onSuccess.
  const finishSignup = useCallback(
    async (result: CompleteSMBSignupResult) => {
      // Always use full authenticate() to ensure Redux state is synced
      // This is critical for:
      // 1. Redux viewer state update
      // 2. Rate display cookie setting
      // 3. Proper authentication persistence
      // ENG-6106: the Bearer flow on a Cognito session refreshes with its ID
      // token instead; the SMS-OTP flow is unchanged (ENG-6115).
      if (otpVerificationToken) await authSession.authenticate(result.token);
      else {
        const origin = signupOrigins.get(result) ?? captureSessionOrigin();
        await authSession.authenticate(await sessionTokenOr(result.token, origin), {
          skipMfaGateRedirect: true,
          origin,
        });
      }

      // Create consultation request
      if (schedulingData) {
        const expertRequestId = parseExpertRequestId(_landingUrl);
        const authClient = authSession.createClient();
        await authClient.client.mutate({
          mutation: REQUEST_CONSULTATION_MUTATION,
          // ENG-6106: mfaSetupAtLogin answers the MFA gate, not mfaGateLink.
          context: { skipMfaGateRedirect: true },
          variables: {
            expert_id: expert.id,
            dates: schedulingData.dates.filter(Boolean),
            duration: schedulingData.duration,
            description: schedulingData.description || undefined,
            engagement_type: EngagementType.Consultation,
            expert_request_id: expertRequestId,
            group_id: result.team?.id || groupId,
          },
        });
      }

      // Clear only after every post-completeSMBSignup step above has
      // actually succeeded. Clearing this earlier (e.g. right after
      // completeSMBSignup itself) let authenticate() or the consultation
      // mutation throwing mint a brand-new key on the next attempt,
      // defeating the guard against a second billing account/subscription
      // (ENG-5334 review fix).
      if (idempotencyScope) clearSubscriptionIdempotencyKey(idempotencyScope);
      // The booking is done. Progress lives in the modal, so clear it here: anything left
      // over would make a later booking in the same modal skip the card form.
      onPaymentProgressChange({
        confirmedClientSecret: null,
        signupResult: null,
        retryBlockedReason: null,
      });

      onSuccess();
    },
    [
      otpVerificationToken,
      idempotencyScope,
      expert.id,
      schedulingData,
      _landingUrl,
      groupId,
      onPaymentProgressChange,
      onSuccess,
    ]
  );

  // Everything after Stripe has confirmed the card, resumed from the step that last
  // failed (ENG-5549). A completeSMBSignup that already returned is not sent again, so
  // a retry after authenticate() or the consultation request failed picks up there.
  const completeSignup = async (
    overridePendingInviteGuard: boolean,
    paymentMethodDetails?: { cardBrand?: string }
  ) => {
    try {
      let result = paymentProgress.signupResult;
      if (!result) {
        const origin = captureSessionOrigin();
        result = await submitCompleteSMBSignup(overridePendingInviteGuard);
        signupOrigins.set(result, origin);
        onPaymentProgressChange({ signupResult: result });
        trackSignupCompleted(result, paymentMethodDetails);
      }
      await finishSignup(result);
    } catch (err) {
      // ENG-6106: a Cognito session the api refuses for MFA setup ends here.
      throw await mfaSetupAtLogin(err);
    }
  };

  // ENG-5334 Piece 2: completeSMBSignup confirmation gate. Remembers the
  // card details from the original Stripe confirmation so the guard's
  // retry (no new card entry) can still pass them through to the
  // signup-completed analytics call.
  const lastPaymentMethodDetailsRef = useRef<{ cardBrand?: string } | undefined>(undefined);
  // Mirrors guard.open for submit, which runs past an await: Escape can close the dialog
  // while a retry is still in flight.
  const guardOpenRef = useRef(false);
  const guard = usePendingInviteGuard<void>({
    submit: async (overridePendingInviteGuard) => {
      // Same latch as runCompletion. Escape can close this dialog mid-retry, and the retry
      // behind it must not start a second completeSMBSignup alongside this one: the api has
      // no in-flight lock, so two could provision two subscriptions (ENG-5549).
      if (isCompletingSignup.current) {
        throw new Error(
          getDefaultErrorMessage(ERROR_CODES.BILLING_IDEMPOTENCY_IN_PROGRESS) ??
            "We're still finalizing your previous attempt. Give us a moment."
        );
      }
      isCompletingSignup.current = true;
      setIsProcessing(true);
      try {
        await completeSignup(overridePendingInviteGuard, lastPaymentMethodDetailsRef.current);
      } catch (err: unknown) {
        // An outcome a retry cannot change ends the dialog and shows on the step instead:
        // for an unregistered expert, every retry would email them again (ENG-5549).
        const blockedReason = getRetryBlockedReason(err);
        if (blockedReason) {
          onPaymentProgressChange({ retryBlockedReason: blockedReason });
          return;
        }
        // The dialog's inline error is never seen once Escape has closed it.
        if (!guardOpenRef.current) {
          captureSelfServiceError(err, { step: 'payment', substep: 'guard_retry' });
          onError(getErrorMessage(err));
          return;
        }
        throw err;
      } finally {
        isCompletingSignup.current = false;
        setIsProcessing(false);
      }
    },
    // No accept-invite action at this call site (v1 limitation): this is an
    // OTP-anonymous booking flow with no authenticated session yet to
    // accept an invite with. Omitting fetchPendingInvite keeps guard.invite
    // null so the dialog never offers that action.
  });
  useEffect(() => {
    guardOpenRef.current = guard.open;
  }, [guard.open]);

  // ENG-5334 follow-up: active_membership_exists previously had no
  // navigation destination, only Cancel/Continue-paid. Mirrors
  // SubscriptionStep.tsx's onGoToProjects; falls back to a direct
  // /expert_requests navigation when the host doesn't supply one.
  const handleGoToProjects = useCallback(() => {
    if (onGoToProjects) {
      onGoToProjects();
      return;
    }
    if (typeof window !== 'undefined') {
      window.location.href = '/expert_requests';
    }
  }, [onGoToProjects]);

  // Handle successful Stripe payment confirmation
  // Unified path: backend auto-detects auth method (OTP token vs Bearer token)
  const handleStripeSuccess = async (paymentMethodDetails?: { cardBrand?: string }) => {
    // Stripe will not confirm this SetupIntent again, so from here on the step offers a
    // retry in place of the card form (ENG-5549). A newly confirmed card is a new attempt.
    if (clientSecret) {
      onPaymentProgressChange({ confirmedClientSecret: clientSecret, retryBlockedReason: null });
    }
    lastPaymentMethodDetailsRef.current = paymentMethodDetails;
    await runCompletion(paymentMethodDetails);
  };

  // Shared by the card form's success callback and the retry shown once the card is on file.
  const runCompletion = async (paymentMethodDetails?: { cardBrand?: string }) => {
    // Idempotency guard: prevent race condition where Stripe calls this handler multiple times
    // or user clicks button before isProcessing state updates
    if (isCompletingSignup.current) return;

    isCompletingSignup.current = true;
    setIsProcessing(true);

    try {
      await completeSignup(false, paymentMethodDetails);
    } catch (err: unknown) {
      // The card is already confirmed at Stripe by this point, and StripePaymentMethodForm
      // leaves isProcessing set on success for its parent to clear. Clear it on every
      // failure path, or the step is stuck on a disabled "Processing..." button (ENG-5549).
      setIsProcessing(false);

      // ENG-5334: pending-invite / active-membership guard codes are
      // intercepted here, before this catch-all mis-tags them as a
      // payment failure.
      if (guard.checkError(err)) return;

      // ENG-5334 review fix: idempotencyKey made this code newly reachable
      // here -- a lost response followed by a client resubmit collides with
      // the still-committing first attempt. That means provisioning is in
      // progress, not failed, so surface the existing recovery copy instead
      // of a payment-failure message/metric.
      if (hasErrorCode(err, ERROR_CODES.BILLING_IDEMPOTENCY_IN_PROGRESS)) {
        onError(
          getDefaultErrorMessage(ERROR_CODES.BILLING_IDEMPOTENCY_IN_PROGRESS) ??
            "We're still finalizing your previous attempt. Give us a moment."
        );
        return;
      }

      const errorMessage = getErrorMessage(err);
      const blockedReason = getRetryBlockedReason(err);
      if (blockedReason) {
        // An outcome to explain, not a failure to report.
        onPaymentProgressChange({ retryBlockedReason: blockedReason });
      } else {
        // The card is attached to the Stripe customer, but the signup or booking did not finish.
        captureSelfServiceError(err, { step: 'payment', substep: 'after_card_confirmed' });
        onError(errorMessage);
      }

      // Track payment failure. Report the API's error code rather than the message, so a
      // pricing failure is distinguishable from a card decline in the funnel (ENG-5571).
      const errorCode = getErrorCode(err) ?? undefined;
      trackSMBError('payment_failed', 'payment', errorMessage, errorCode);
    } finally {
      // Reset flag to allow retry if there was an error
      isCompletingSignup.current = false;
    }
  };

  // ENG-5334 Piece 2 booking-flow scope: check for a pending project invite
  // right after the SetupIntent's clientSecret arrives, before the Stripe
  // card form ever renders, so an invited user isn't asked for real card
  // details before being blocked. Skipped for the returning-user-with-
  // billing view above (that path never calls completeSMBSignup at all).
  // Fails open on a broken check — the completeSMBSignup guard above is
  // the backstop. No accept-invite action here either (same v1 limitation
  // as the guard above); the only choice offered is continuing paid.
  const [earlyInviteCheck, setEarlyInviteCheck] = useState<
    | { status: 'checking' }
    | { status: 'clear' }
    | { status: 'blocked'; invite: PendingProjectInvite }
    | { status: 'blocked-membership' }
  >({ status: 'checking' });
  // ENG-5549: once Stripe has confirmed the card (or the signup itself went through), the
  // card form can only fail, so retry the remaining steps without it.
  const isCardOnFile = hasCardOnFile(paymentProgress, clientSecret);
  // The early check only protects card entry, so it has nothing to do once the card is on
  // file; the completeSMBSignup guard still covers the retry (ENG-5549).
  const skipEarlyInviteCheck =
    (isReturningUser && !!billingAccount && !isExistingUserWithoutBilling) || isCardOnFile;

  const { fetchPendingInvite } = usePendingProjectInvite({
    runQuery: useCallback(async (token?: string) => {
      const client = token ? new GraphQLClient(config.apiUrl) : authSession.createClient();
      // ENG-5334 review fix: use the GraphQLClient wrapper instead of the
      // raw Apollo client.client.query() -- the wrapper runs errors through
      // handleError (401 -> handleSessionExpiry(), normalized APIError)
      // that the rest of this flow assumes.
      const data = await client.query(
        PENDING_PROJECT_INVITE_QUERY,
        token ? { otpVerificationToken: token } : undefined,
        { fetchPolicy: 'network-only' }
      );
      return data?.pendingProjectInvite ?? null;
    }, []),
  });

  // ENG-5334 follow-up: parity with SubscriptionStep.tsx's early
  // active-membership check. Same booking-flow v1 limitation as the
  // pending-invite check above — no accept action, just continue paid or
  // go to /expert_requests.
  const fetchActiveProjectMembership = useCallback(async (token?: string) => {
    const client = token ? new GraphQLClient(config.apiUrl) : authSession.createClient();
    // ENG-5334 review fix: same wrapper switch as fetchPendingInvite above.
    const data = await client.query(
      ACTIVE_PROJECT_MEMBERSHIP_QUERY,
      token ? { otpVerificationToken: token } : undefined,
      { fetchPolicy: 'network-only' }
    );
    return data?.activeProjectMembership ?? false;
  }, []);

  useEffect(() => {
    if (skipEarlyInviteCheck) {
      setEarlyInviteCheck({ status: 'clear' });
      return;
    }
    if (!clientSecret) return;
    let cancelled = false;
    fetchPendingInvite(otpVerificationToken || undefined)
      .then(async (invite) => {
        if (cancelled) return;
        if (invite) {
          setEarlyInviteCheck({ status: 'blocked', invite });
          return;
        }
        try {
          const hasActiveMembership = await fetchActiveProjectMembership(
            otpVerificationToken || undefined
          );
          if (cancelled) return;
          setEarlyInviteCheck(
            hasActiveMembership ? { status: 'blocked-membership' } : { status: 'clear' }
          );
        } catch (err: unknown) {
          if (cancelled) return;
          captureSelfServiceError(err, {
            step: 'payment',
            substep: 'early_active_membership_check',
          });
          setEarlyInviteCheck({ status: 'clear' });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        captureSelfServiceError(err, { step: 'payment', substep: 'early_pending_invite_check' });
        setEarlyInviteCheck({ status: 'clear' });
      });
    return () => {
      cancelled = true;
    };
  }, [
    skipEarlyInviteCheck,
    clientSecret,
    otpVerificationToken,
    fetchPendingInvite,
    fetchActiveProjectMembership,
  ]);

  // V2: Consultation order summary
  const renderConsultationSummary = () => {
    const creditPrice = calculateCreditPrice();
    const creditsDisplay = creditPrice === null ? RATE_UNAVAILABLE_LABEL : `${creditPrice} credits`;
    const dollarPrice = calculatePrice();
    const dollarDisplay = dollarPrice === null ? RATE_UNAVAILABLE_LABEL : `$${dollarPrice}`;
    const priceDisplay = isCreditsPayment ? creditsDisplay : dollarDisplay;

    return (
      <div className={s.orderSummary}>
        <h4 className={s.orderSummaryTitle}>
          {isReturningUser ? 'Booking Summary' : 'Order Summary'}
        </h4>
        <div className={s.orderSummaryRow}>
          <span className={s.orderSummaryLabel}>Expert</span>
          <span className={s.orderSummaryValue}>{expert.name}</span>
        </div>
        <div className={s.orderSummaryRow}>
          <span className={s.orderSummaryLabel}>Duration</span>
          <span className={s.orderSummaryValue}>{duration} min</span>
        </div>
        <div className={s.orderSummaryDivider} />
        <div className={s.orderSummaryRow}>
          <span className={s.orderSummaryLabel}>Due when confirmed</span>
          <span className={s.orderSummaryTotal}>{priceDisplay}</span>
        </div>
      </div>
    );
  };

  // Render logged-in banner for returning users
  const renderLoggedInBanner = () => {
    if (!isReturningUser || !userName) return null;

    return (
      <div
        style={{
          backgroundColor: '#F0FDFA',
          border: '1px solid #99F6E4',
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
        }}
      >
        <p style={{ fontSize: 14, color: '#0F766E', margin: 0 }}>
          <span>Logged in as </span>
          <strong>{userName}</strong>
          {userCompany && <span> ({userCompany})</span>}
        </p>
      </div>
    );
  };

  // Handle confirm booking for returning users
  const handleConfirmBooking = async () => {
    if (!isReturningUser) return;
    // Same invariant as submitCompleteSMBSignup, for the path that skips it: requestConsultation
    // hard-fails on an unpriceable expert, so don't send it (ENG-5571).
    if (rateUnavailable) {
      onError(RATE_UNAVAILABLE_SUBMIT_MESSAGE);
      return;
    }

    setIsProcessing(true);

    try {
      // For returning users, just create the consultation request
      // The user is already authenticated
      if (schedulingData) {
        const expertRequestId = parseExpertRequestId(_landingUrl);
        const client = authSession.createClient();
        await client.client.mutate({
          mutation: REQUEST_CONSULTATION_MUTATION,
          variables: {
            expert_id: expert.id,
            dates: schedulingData.dates.filter(Boolean),
            duration: schedulingData.duration,
            description: schedulingData.description || undefined,
            engagement_type: EngagementType.Consultation,
            expert_request_id: expertRequestId,
            group_id: groupId,
          },
        });
      }

      // Track returning user booking success
      const totalDuration = modalOpenTime
        ? Math.round((new Date().getTime() - modalOpenTime.getTime()) / 1000)
        : 0;
      trackSMBSignupComplete(totalDuration, true, expert.id, userId || '', '');

      onSuccess();
    } catch (err: unknown) {
      console.error('Booking error:', err);
      const errorMessage = getErrorMessage(err);
      onError(errorMessage);

      // Track booking error (for returning users) by API error code, not message (ENG-5571).
      const errorCode = getErrorCode(err) ?? undefined;
      trackSMBError('api_error', 'payment', errorMessage, errorCode);
    } finally {
      setIsProcessing(false);
    }
  };

  // Render payment method info for returning users (no Change button)
  // - postpaid = SMB customers (show card info)
  // - prepaid = Credit customers (show "Billed to {team}")
  const renderPaymentMethod = () => {
    if (!billingAccount) return null;

    // SMB customers (postpaid): show card info
    if (billingAccount.paymentType === 'postpaid' && billingAccount.defaultCard) {
      const { brand, last4 } = billingAccount.defaultCard;
      return (
        <div className={s.orderSummary} style={{ marginTop: 16 }}>
          <h4 className={s.orderSummaryTitle}>Payment Method</h4>
          <div className={s.orderSummaryRow}>
            <span className={s.orderSummaryLabel}>Card</span>
            <span className={s.orderSummaryValue}>
              {brand} •••• {last4}
            </span>
          </div>
        </div>
      );
    }

    // Credit customers (prepaid): show "Billed to {company/team name}."
    return (
      <div className={s.orderSummary} style={{ marginTop: 16 }}>
        <h4 className={s.orderSummaryTitle}>Payment Method</h4>
        <p style={{ fontSize: 14, color: 'var(--onSurfaceMuted)', margin: 0 }}>
          <span>{`Billed to ${userCompany || 'Your Account'}.`}</span>
        </p>
      </div>
    );
  };

  // While processing, the button keeps its fill and shows a spinner; the grey disabled
  // look read as "unavailable" rather than "working" (ENG-5549). The spinner needs sx: the
  // theme pins MuiCircularProgress to dark green whatever `color` says.
  const renderSubmitButton = (onClick: () => void, label: string) => (
    <Button
      color="teal"
      size="normal"
      onClick={onClick}
      disabled={isProcessing || rateUnavailable}
      aria-busy={isProcessing}
      startIcon={
        isProcessing ? (
          <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
        ) : undefined
      }
      classes={{ root: cx(s.continueButton, isProcessing && s.continueButtonProcessing) }}
    >
      {isProcessing ? 'Processing...' : label}
    </Button>
  );

  // Render returning user payment view (with existing billing account)
  // Skip this if user has no billing - they need to add payment via Stripe form
  // Note: billingAccount check ensures pending-resuming users (who don't have billing) show the Stripe form
  if (isReturningUser && billingAccount && !isExistingUserWithoutBilling) {
    return (
      <>
        <div className={s.schedulingContent} style={{ paddingTop: 24 }}>
          {renderConsultationSummary()}
          {renderPaymentMethod()}
          {renderRateUnavailableNotice()}

          {paymentError && (
            <div className={s.paymentError}>
              <p className={s.paymentErrorText}>{paymentError}</p>
            </div>
          )}
        </div>

        <div className={s.footer}>
          <Button
            variant="text"
            size="normal"
            onClick={onBack}
            disabled={isProcessing}
            classes={{ root: s.backButton }}
          >
            Back
          </Button>
          {renderSubmitButton(handleConfirmBooking, 'Submit Request')}
        </div>
      </>
    );
  }

  // New user payment view OR existing user without billing (needs Stripe form)
  // All users adding a card start a free trial
  const submitText = 'Start Free Trial';

  const renderTrialBanner = () => (
    <div className={s.trialBanner}>
      <FAIcon iconSet="fal" icon="clock" size={20} className={s.trialBannerIcon} />
      <div className={s.trialBannerContent}>
        <p className={s.trialBannerTitle}>14-day free trial starts today</p>
        <p className={s.trialBannerSubtitle}>
          After {trialEndDate}: $300/month billed to this card. Cancel anytime.
        </p>
      </div>
    </div>
  );

  const { signupResult, retryBlockedReason } = paymentProgress;

  // Nothing extra while a submit is running: on a first attempt that succeeds, this view is
  // only on screen between Stripe confirming and the confirmation screen.
  const renderCardOnFileNote = () => {
    if (isProcessing) return null;
    if (!signupResult) return <p className={s.cardOnFileNote}>Your card is saved.</p>;
    if (retryBlockedReason) {
      return <p className={s.cardOnFileNote}>Your free trial has started.</p>;
    }
    return (
      <p className={s.cardOnFileNote}>
        Your free trial has started. Submit your request to finish booking.
      </p>
    );
  };

  // The previous attempt's error would read as the retry failing, so hide it while one runs.
  const cardOnFileError = retryBlockedReason ?? (isProcessing ? null : paymentError);

  const renderCardOnFile = () => (
    <>
      <div className={s.formContent}>
        {renderConsultationSummary()}
        {renderRateUnavailableNotice()}
        {/* The trial starts when completeSMBSignup returns, so the offer stands until then,
            unless the step can no longer retry: then nothing will start it (ENG-5925). */}
        {!signupResult && !retryBlockedReason && renderTrialBanner()}
        {renderCardOnFileNote()}
        {cardOnFileError && (
          <div className={s.paymentError} role="alert">
            <p className={s.paymentErrorText}>{cardOnFileError}</p>
          </div>
        )}
        {retryBlockedReason && SIGN_IN_REASONS.includes(retryBlockedReason) && (
          <Link href="/login">Go to sign in</Link>
        )}
      </div>
      <div className={s.footer}>
        <Button
          variant="text"
          size="normal"
          onClick={onBack}
          disabled={isProcessing}
          classes={{ root: s.backButton }}
        >
          Back
        </Button>
        {!retryBlockedReason &&
          renderSubmitButton(
            () => void runCompletion(lastPaymentMethodDetailsRef.current),
            signupResult ? 'Submit Request' : submitText
          )}
      </div>
    </>
  );

  return (
    <>
      {/* Show logged-in banner for existing users without billing */}
      {isExistingUserWithoutBilling && renderLoggedInBanner()}

      {isCardOnFile ? (
        renderCardOnFile()
      ) : (
        <StripePaymentMethodForm
          clientSecret={earlyInviteCheck.status === 'clear' ? clientSecret : null}
          source="partner_signup"
          onSuccess={handleStripeSuccess}
          onError={onError}
          isProcessing={isProcessing}
          setIsProcessing={setIsProcessing}
          paymentError={paymentError}
          showBackButton={true}
          onBack={onBack}
          submitButtonText={submitText}
          processingButtonText="Processing..."
          submitDisabled={rateUnavailable}
          // The trial banner states that the trial has started and names a billing
          // date. If the card form never loaded, no card was captured and no trial
          // began, so the failure state keeps the summary (which reads as a quote)
          // and drops the banner.
          failureChildren={renderConsultationSummary()}
        >
          {/* Consultation summary */}
          {renderConsultationSummary()}
          {renderRateUnavailableNotice()}

          {/* Trial banner - show for all users adding a card */}
          {renderTrialBanner()}
        </StripePaymentMethodForm>
      )}

      {/* ENG-5334: pre-card-entry block. No accept-invite action at this
          call site (v1 limitation, see plan) — only continuing paid.
          ENG-5334 code-review follow-up: ConfirmDialog's onCancel is also
          MUI's Escape/backdrop-click channel (componentsv2/Dialog wires
          onClose straight to it), so the non-mutating "Back" action lives
          there. Escape must never be able to reveal real card entry.
          "Start new subscription" only requires an explicit click, in the
          destructive-red confirm slot. Mirrors SubscriptionStep.tsx's fix
          for the same two-action shape. */}
      {earlyInviteCheck.status === 'blocked' && (
        <ConfirmDialog
          open
          dialogTitle="You're already invited to a project"
          message={`We found a pending invite for you to join ${earlyInviteCheck.invite.projectName}. Joining a project is free, but this flow can't accept it here. Continue below to set up a paid subscription instead, or go back and accept the invite after signing in.`}
          confirmButtonLabel="Start new subscription"
          cancelButtonLabel="Back"
          onConfirm={() => setEarlyInviteCheck({ status: 'clear' })}
          onCancel={onBack}
        />
      )}

      {/* ENG-5334 follow-up: early, pre-card-entry active-membership block,
          parity with SubscriptionStep.tsx. No accept action at this call
          site (same v1 limitation as the pending-invite block above). No
          invite to accept here, so the safe onCancel/Escape action goes to
          the user's projects rather than "Back", same Escape-hardening as
          the pending-invite dialog above: onCancel must never land on the
          un-blocking "Start new subscription" action, which requires an
          explicit click in the destructive-red confirm slot instead.
          whitespace-normal overrides componentsv2/Button's hardcoded
          whitespace-nowrap so neither label is forced onto one line —
          without it, the two button labels together force the dialog
          wider than the viewport (horizontal scroll). w-[calc(100%-64px)]
          accounts for componentsv2/Dialog's own 32px MUI margin on each
          side (w-full would collapse that gutter below ~664px viewports),
          since this copy runs longer than this dialog family's usual
          one-liner. */}
      {earlyInviteCheck.status === 'blocked-membership' && (
        <ConfirmDialog
          open
          dialogTitle={ACTIVE_MEMBERSHIP_TITLE}
          message={
            <Stack spacing={1.5} sx={{ textAlign: 'left' }}>
              <Typography variant="body2">{ACTIVE_MEMBERSHIP_MESSAGE_LINE_1}</Typography>
              <Typography variant="body2">{ACTIVE_MEMBERSHIP_MESSAGE_LINE_2}</Typography>
            </Stack>
          }
          confirmButtonLabel="Start new subscription"
          cancelButtonLabel={GO_TO_PROJECTS_LABEL}
          onConfirm={() => setEarlyInviteCheck({ status: 'clear' })}
          onCancel={handleGoToProjects}
          className="[&_.MuiDialog-paper]:w-[calc(100%-64px)]"
          confirmButtonProps={{ className: '!whitespace-normal' }}
          cancelButtonProps={{ className: '!whitespace-normal' }}
        />
      )}

      {/* ENG-5334 Piece 2: completeSMBSignup confirmation gate. Same v1
          limitation — continuing paid is the only action offered.
          ENG-5334 code-review follow-up: unlike the pre-card-entry block
          above, "Start new subscription" here triggers a real
          completeSMBSignup mutation (guard.retry()), so it deliberately
          stays on onConfirm/confirmButtonLabel (explicit click only) rather
          than swapping into cancelButtonLabel/onCancel — onCancel is also
          MUI Dialog's implicit dismiss channel (Escape/backdrop-click), and
          binding a mutating action there would mean an accidental dismiss
          gesture silently completes signup, exactly the failure mode this
          same review flagged as a must-fix elsewhere on this ticket. Cancel
          stays the safe, non-mutating action in both the explicit button
          and the implicit-dismiss channel. */}
      {guard.open && (
        <ConfirmDialog
          open={guard.open}
          dialogTitle={
            guard.code === ERROR_CODES.SMB_SIGNUP_PENDING_PROJECT_INVITE
              ? "You're already invited to a project"
              : ACTIVE_MEMBERSHIP_TITLE
          }
          message={
            <Stack spacing={1.5} sx={{ textAlign: 'left' }}>
              {guard.code === ERROR_CODES.SMB_SIGNUP_PENDING_PROJECT_INVITE ? (
                <Typography variant="body2">
                  We found a pending invite for you to join a project. Joining is free, but this
                  flow can&apos;t accept it here. Continue with a paid subscription instead, or
                  accept the invite from your dashboard after signing in.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2">{ACTIVE_MEMBERSHIP_MESSAGE_LINE_1}</Typography>
                  <Typography variant="body2">{ACTIVE_MEMBERSHIP_MESSAGE_LINE_2}</Typography>
                </>
              )}
              {guard.retryError && <p className={s.paymentErrorText}>{guard.retryError}</p>}
              {/* ENG-5334 code-review follow-up: active_membership_exists
                  previously had no way out besides Cancel/Continue-paid.
                  This is the legacy components/Button (not
                  componentsv2/Button), which wraps text normally, so it
                  doesn't need the whitespace-normal override the dialog's
                  own confirm/cancel buttons need below. */}
              {guard.code === ERROR_CODES.SMB_SIGNUP_ACTIVE_MEMBERSHIP_EXISTS && (
                <Button variant="text" size="normal" onClick={handleGoToProjects}>
                  {GO_TO_PROJECTS_LABEL}
                </Button>
              )}
            </Stack>
          }
          confirmButtonLabel="Start new subscription"
          cancelButtonLabel="Cancel"
          onConfirm={() => {
            // retry() rethrows on failure (retryError is already surfaced
            // inline above) so we can still report it via
            // captureSelfServiceError, matching SubscriptionStep.tsx's
            // handleGuardRetry.
            guard.retry().catch((err: unknown) => {
              captureSelfServiceError(err, { step: 'payment', substep: 'guard_retry' });
            });
          }}
          onCancel={guard.cancel}
          className="[&_.MuiDialog-paper]:w-[calc(100%-64px)]"
          confirmButtonProps={{
            loading: guard.retrying,
            disabled: guard.retrying,
            className: '!whitespace-normal',
          }}
          cancelButtonProps={{ disabled: guard.retrying, className: '!whitespace-normal' }}
        />
      )}
    </>
  );
}

export default PaymentStep;
